<template>
  <div class="masonry-grid" ref="masonryGrid" :class="{ faded: !isShown }">
    <div :class="gridSizerClass" />
    <transition-group class="masonry-grid__items" name="fade" tag="div">
      <slot />
    </transition-group>
  </div>
</template>

<script>
  import Masonry from "masonry-layout";
  import { mapState } from "vuex";
  import { useIsMobile } from "./../composables/useIsMobile";

  export default {
    beforeUnmount() {
      const grid = this.$refs.masonryGrid;
      grid.removeEventListener("load", this.onMediaSettled, true);
      grid.removeEventListener("error", this.onMediaSettled, true);
      cancelAnimationFrame(this.mediaLayoutFrame);

      if (this.masonryInstance) {
        this.masonryInstance.destroy();
        this.$emit("destroyed");
      }

      window.removeEventListener("resize", this.checkInitializationMasonry);
    },

    computed: {
      ...mapState("comments", ["preventRelayoutingMasonry"]),
    },

    data() {
      return {
        isShown: false,
        masonryInstance: null,
        mediaLayoutFrame: null,
        // Seed from the mount-time list: the component mounts with the
        // comments already present, so an empty seed would make the first
        // list change (e.g. a sort) read as "items were added" and trigger
        // the created-comment scroll below.
        oldList: this.list.slice(0),
      };
    },

    methods: {
      checkInitializationMasonry() {
        if (this.isMobile) {
          this.showGrid();
        } else {
          this.initMasonry();
        }

        if (this.isMobile && this.masonryInstance) {
          this.masonryInstance.destroy();
          this.$nextTick(() => {
            this.masonryInstance = null;
          });
        }
      },

      initMasonry() {
        setTimeout(() => {
          this.masonryInstance = new Masonry(this.$refs.masonryGrid, {
            columnWidth: `.${this.gridSizerClass}`,
            gutter: this.gutter,
            itemSelector: `.${this.gridItemClass}`,
            percentPosition: true,
          });

          this.oldList = this.list.slice(0);

          this.showGrid();
        }, 500);
      },
      // An image that loads or fails after the timed layouts can change a card's height.
      onMediaSettled() {
        if (
          !this.masonryInstance ||
          this.preventRelayoutingMasonry ||
          this.mediaLayoutFrame
        ) {
          return;
        }

        this.mediaLayoutFrame = requestAnimationFrame(() => {
          this.mediaLayoutFrame = null;

          if (this.masonryInstance) {
            this.masonryInstance.reloadItems();
            this.masonryInstance.layout();
          }
        });
      },

      // we need it because the layout is measuring elements
      // and it's hard to predict time for finishing rendering element
      reLayoutInLoop(times, time, delay = 500) {
        setTimeout(() => {
          let masonryLayoutCount = 1;

          const initMasonryInterval = setInterval(() => {
            if (masonryLayoutCount > times) {
              clearInterval(initMasonryInterval);
            }

            if (this.masonryInstance) {
              // Masonry captures its item order from the DOM once at init and
              // layout() reuses that frozen order, so re-read the DOM first or
              // reordered lists (e.g. sorting) keep their old visual positions.
              this.masonryInstance.reloadItems();
              this.masonryInstance.layout();
              masonryLayoutCount += 1;
            }
          }, time);
        }, delay);
      },

      showGrid() {
        this.isShown = true;

        // it's the delay of fading to trigger an event when the grid is completely shown
        setTimeout(() => {
          this.$emit("updated");
        }, 200);
      },
    },

    mounted() {
      this.checkInitializationMasonry();

      window.addEventListener("resize", this.checkInitializationMasonry);
      // load and error don't bubble, so listen in the capture phase.
      const grid = this.$refs.masonryGrid;
      grid.addEventListener("load", this.onMediaSettled, true);
      grid.addEventListener("error", this.onMediaSettled, true);
    },
    props: {
      gridItemClass: {
        default: "grid-item",
        type: String,
      },
      gridSizerClass: {
        default: "grid-sizer",
        type: String,
      },
      gutter: {
        default: 30,
        type: Number,
      },
      list: {
        default: () => [],
        type: Array,
      },
    },
    setup() {
      const { isMobile, isTablet, setWindowWidth, isMobileDevice } =
        useIsMobile();
      return { isMobile, isMobileDevice, isTablet, setWindowWidth };
    },

    watch: {
      list(newList) {
        // if it's adding in a list
        if (this.oldList.length < newList.length) {
          if (this.masonryInstance) {
            setTimeout(() => {
              const card = document.querySelector(`.${this.gridItemClass}`);

              // The timed layouts' reloadItems() may have added it already; a
              // second item would end its animation in the old position.
              if (!this.masonryInstance.getItem(card)) {
                this.masonryInstance.prepended(card);
              }
            }, 300);
          } else {
            // if there is mobile version and added new item to the start
            const scrollTop =
              document
                .querySelector(`.${this.gridSizerClass}`)
                .getBoundingClientRect().top +
              window.innerHeight / 2;

            if (scrollTop > window.scrollY) {
              window.scrollTo({
                behavior: "smooth",
                top: scrollTop,
              });
            }
          }
        }

        this.oldList = newList.slice(0);

        if (this.masonryInstance && !this.preventRelayoutingMasonry) {
          this.reLayoutInLoop(2, 200, 0);
        }
      },

      masonryInstance(newInstance) {
        if (!this.isMobile && newInstance) {
          this.reLayoutInLoop(2, 200, 500);
        }
      },
    },
  };
</script>
